import React, { useRef, useState, useContext, useEffect } from 'react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import getUrlParam, { getUrlParamRaw, decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useNavigate, useLocation } from 'react-router';
import ConfigData from 'config/ConfigData';
import { store } from 'components/Store';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import Loading from 'components/Login/Loading';
import ChangeKeywordsExclusions from 'components/Dashboard/utilities/changeKeywordExclusions';
import returnParamValue from 'components/Dashboard/helpers/returnParamValue';
import useResetReference from 'components/Dashboard/SubNav/useResetReference';
import useCompileInitialFilters from 'components/Dashboard/Filters/useCompileInitialFilters';
import useCompileFiltersFunction from 'components/Dashboard/Filters/useCompileFilters';
import addQuotesToString from 'components/Dashboard/utilities/addQuotesToString';
import JSONBigInt from 'json-bigint';
import createSectionViewStoreProperty from 'components/Dashboard/ParliamentLive/createSectionViewStoreProperty';
import { parseSearchQuery, removeWrappingQuotes, unescapeDoubleQuotes } from 'utils/stringFunctions';
import ChangeKeywordsCategory from 'components/Dashboard/utilities/changeKeywordsCategory';
import useCreateSections from 'components/Dashboard/hooks/useCreateSections';
import removeDuplicateFilters from 'components/Dashboard/Filters/removeDuplicateFilters';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import { useAuth0 } from '@auth0/auth0-react';
import replaceInvertedQuotes from 'components/Dashboard/utilities/replaceInvertedQuotes';

const replaceCharacter = ({ item, character }) => {
  return item !== null && item !== undefined ? String(item).replaceAll(character, '').trim() : item;
};

const SearchResults = () => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { search } = state;

  const [isLoading, setIsLoading] = useState(true);
  const totalResultsCall = useRef(null);
  const numberOfResources = 3;

  const { generalApiCall } = useGeneralApiCall();
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const { resetReference } = useResetReference();
  const { compileInitialFilters } = useCompileInitialFilters();
  const { compileFiltersFunction, activeResultsConditional } = useCompileFiltersFunction();
  const { createSections } = useCreateSections();
  const { clearContentFilters } = useFilters();
  const { customContactsCall } = useCustomContactsCall();

  const searchParam = getUrlParamRaw('or') ?? getUrlParamRaw('search');

  const detectAdvancedSearch = () => {
    let searchQuery = decodeUrlQueryValue(searchParam) ?? '';
    const wrappedInQuotes = parseSearchQuery(searchQuery)?.length === 1;
    let advancedSearch = searchQuery.includes(',') && !wrappedInQuotes;
    let keywords = wrappedInQuotes
      ? [searchQuery.substring(1, searchQuery?.length - 1)]
      : parseSearchQuery(searchQuery);
    keywords = keywords.map((item) => {
      return removeWrappingQuotes(unescapeDoubleQuotes(item));
    });

    let urlSearch = `${location.search}${location.hash}`;
    if (advancedSearch) {
      urlSearch = `?or=${encodeURIComponent(keywords.map((item) => `"${item}"`).join(','))}${
        getUrlParam('and') ? `&and=${getUrlParam('and')}` : ''
      }${getUrlParam('not') ? `&not=${getUrlParam('not')}` : ''}${location.hash}`;
    }

    return { advancedSearch, keywords, urlSearch };
  };

  const individualApiCall = async (item, sourceProp) => {
    const { type, name, filters, collapseField } = item;
    const initialFilters = compileInitialFilters('search-results');
    const referenceFilters = initialFilters.filter(
      (filter) => filter.pages.includes(name) && activeResultsConditional(filter, initialFilters)
    );
    const hashFilters = compileFiltersFunction(referenceFilters);

    const apiType = type ?? name;

    const pathname = '/api/search/query';
    const method = 'post';
    //AE: CHECK HERE THE TWEETS VALIDATIONS
    const requestHeaders = {
      transformResponse: [(data) => data],
      signal: sourceProp.signal,
    };

    const query =
      apiType === 'stakeholder'
        ? addQuotesToString(decodeUrlQueryValue(searchParam))
        : getUrlParamRaw('or')
          ? ChangeKeywordsCategory(parseSearchQuery(decodeUrlQueryValue(searchParam)))
          : decodeUrlQueryValue(searchParam);

    let requestProperties = {
      type: apiType,
      pageSize: numberOfResources,
      pageNumber: 1,
      filters: [],
      includeHighlights: true,
      query: replaceInvertedQuotes(query),
    };

    if (getUrlParam('and')) {
      requestProperties = {
        ...requestProperties,
        andQueries: ChangeKeywordsExclusions(decodeUrlQueryValue(getUrlParamRaw('and')).split(',')),
      };
    }
    if (getUrlParam('not')) {
      requestProperties = {
        ...requestProperties,
        mustNotQueries: ChangeKeywordsExclusions(decodeUrlQueryValue(getUrlParamRaw('not')).split(',')),
      };
    }

    if (filters) {
      requestProperties = { ...requestProperties, filters: [...requestProperties.filters, ...filters] };
    }

    if (hashFilters?.filters?.length > 0) {
      requestProperties = { ...requestProperties, filters: [...requestProperties.filters, ...hashFilters?.filters] };
    }

    if (collapseField) {
      requestProperties = { ...requestProperties, collapseField };
    }

    if (requestProperties.filters?.length > 0) {
      requestProperties.filters = removeDuplicateFilters(requestProperties.filters);
    }

    let results = await generalApiCall({
      pathname,
      method,
      requestHeaders,
      requestProperties,
      returnCompleteRequest: true,
      notShowErrorMessage: true,
      bigInt: true,
    });
    if (results?.data) {
      return JSONBigInt.parse(results.data);
    } else {
      throw Error('bad search result');
    }
  };

  const sentimentPage = () => {
    let url = !!document.referrer ? new URL(document.referrer) : null;
    return url?.pathname === '/sentiment';
  };

  const stakeholderNameCondition = (results) => {
    for (let i = 0; i < results.hits.length; i++) {
      let highlightedTitle = results.highlights[i].highlightedTitle.toLowerCase();
      let tags = results.hits[i].tags?.map((tag) => tag.toLowerCase());
      let value = decodeUrlQueryValue(searchParam).toLowerCase().trim();
      if (
        tags?.some((tag) => tag.toLowerCase() === value) ||
        highlightedTitle.includes("<em class='policymogul-keyword-match'>")
      ) {
        return true;
      }
    }
    return false;
  };

  totalResultsCall.current = async (source) => {
    try {
      setIsLoading(true);
      clearContentFilters();
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'customContactsOnSearch',
        value: false,
      });
      const { urlSearch } = detectAdvancedSearch();
      if (!!searchParam) {
        //OP: reset the section view to the default value for search results
        dispatch({ type: 'MODIFY_SECTION', parameter: 'sectionsView', value: createSectionViewStoreProperty() });

        const sectionsToCall = createSections();
        if (!getUrlParamRaw('or')) {
          const stakeholderResults = await individualApiCall(
            {
              name: 'Stakeholders',
              type: 'stakeholder',
            },
            source
          );
          if (stakeholderResults?.hits.length > 0) {
            if (stakeholderNameCondition(stakeholderResults) && !sentimentPage()) {
              applySearch();
              navigate(`/search-results/stakeholders${urlSearch}${location.hash}`, { replace: true });
              return;
            }
          }
          if (isAuthenticated) {
            const customContacts = await customContactsCall({ source, numberOfResources });
            if (customContacts?.hits?.length > 0) {
              dispatch({
                type: 'MODIFY_SECTION',
                parameter: 'customContactsOnSearch',
                value: true,
              });
              applySearch();
              navigate(`/search-results/custom-contacts${urlSearch}${location.hash}`, { replace: true });
            }
          }
        }
        for (let i = 0; i < sectionsToCall.length; i++) {
          let section = sectionsToCall[i];
          const { name, urlName } = section;
          let results = await individualApiCall(section, source);
          if (results?.hits?.length > 0) {
            applySearch();
            navigate(`/search-results/${normalize(TransformFromPascalCase(urlName ?? name))}${urlSearch}`, {
              replace: true,
            });
            return;
          }
        }
        applySearch();
        navigate(`/search-results/${ConfigData?.monitorInboxInitialPage ?? 'key-updates'}${urlSearch}`, {
          replace: true,
        });
      } else {
        resetReference();
        dispatch({ type: 'MODIFY_SECTION', parameter: 'sectionsView', value: createSectionViewStoreProperty() });
        navigate(
          {
            pathname: `/search-results/${ConfigData?.monitorInboxInitialPage ?? 'key-updates'}`,
            search: location.search,
          },
          {
            replace: true,
          }
        );
      }
    } catch (error) {}
  };

  const applySearch = () => {
    const { advancedSearch, keywords } = detectAdvancedSearch();
    if (advancedSearch || getUrlParam('or')) {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'referenceState',
        value: {
          activeReference: true,
          keywords: advancedSearch ? keywords : returnParamValue('or'),
          andKeywords: returnParamValue('and'),
          keywordExclusions: returnParamValue('not'),
        },
      });
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'search',
        value: {
          ...search,
          activeSearch: false,
          query: '',
          list: null,
        },
      });
    } else {
      let keywordsValue = returnParamValue('search').filter(
        (item) => item.startsWith('"') || item.startsWith(`'`) || item.trim().split(' ').length === 1
      );
      keywordsValue = keywordsValue.map((item) => {
        return removeWrappingQuotes(unescapeDoubleQuotes(item));
      });
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'referenceState',
        value: {
          activeReference: false,
          keywords: keywordsValue,
          andKeywords: [],
          keywordExclusions: [],
        },
      });
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'search',
        value: {
          ...search,
          activeSearch: true,
          query: decodeUrlQueryValue(getUrlParamRaw('search')),
          list: null,
        },
      });
    }
  };

  useEffect(() => {
    const source = new AbortController();
    totalResultsCall.current(source);
    return () => {
      source.abort('Traditional search tab cancelled by the user');
    };
  }, [searchParam]);

  return <>{isLoading && <Loading />}</>;
};

const useCustomContactsCall = () => {
  const { generalApiCall } = useGeneralApiCall();

  const customContactsCall = async ({ source, numberOfResources = 20 }) => {
    try {
      const pathname = '/api/crm-contact/query';
      const method = 'post';
      const result = await generalApiCall({
        pathname,
        method,
        requestProperties: {
          pageSize: numberOfResources,
          pageNumber: 1,
          filters: [{ field: 'categories', value: 'Custom Contact', operator: 'str_eq' }],
          query: decodeUrlQueryValue(getUrlParamRaw('search')),
        },
        needsAuthentication: true,
        notShowErrorMessage: true,
        source,
      });
      return result;
    } catch (error) {}
  };

  return { customContactsCall };
};

export { replaceCharacter, useCustomContactsCall };
export default SearchResults;
